import { DateTime } from "luxon";
import { Appointment } from "../models/admissions.model";
import { ProcedureRoomListing } from "../models/procedure-room.model";

/** True when a procedure-room row has a real check-in time on the listing or encounter. */
export function hasProcedureRoomActualCheckIn(
  source: ProcedureRoomListing | Appointment | null | undefined
): boolean {
  if (!source) {
    return false;
  }
  const raw =
    "encounterSchedule" in source
      ? (source as Appointment).actualCheckIn ??
        (source as Appointment).encounterSchedule?.actualCheckIn
      : (source as ProcedureRoomListing).actualCheckIn;
  if (raw == null) {
    return false;
  }
  const value = String(raw).trim();
  return !!value && value.toLowerCase() !== "null";
}

/** Resolve schedule DOS (`yyyy-MM-dd`) from encounter schedule or encounter datetime. */
export function resolveProcedureRoomScheduleDate(
  listing: ProcedureRoomListing | null | undefined,
  encounter: Appointment | null | undefined
): string {
  const scheduleDate = String(
    encounter?.encounterSchedule?.scheduleDate ?? ""
  ).trim();
  if (scheduleDate) {
    return scheduleDate.split("T")[0];
  }

  const encounterDateTime = String(
    encounter?.encounterDateTime ?? listing?.encounterDateTime ?? ""
  ).trim();
  if (!encounterDateTime) {
    return "";
  }

  const parsed = DateTime.fromISO(encounterDateTime);
  if (parsed.isValid) {
    return parsed.toISODate() || "";
  }

  const dateOnly = encounterDateTime.split("T")[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(dateOnly) ? dateOnly : "";
}

/** Auto check-in applies only when the encounter DOS is the current local calendar day. */
export function isProcedureRoomScheduleDateToday(
  listing: ProcedureRoomListing | null | undefined,
  encounter: Appointment | null | undefined,
  now: DateTime = DateTime.now()
): boolean {
  const scheduleDate = resolveProcedureRoomScheduleDate(listing, encounter);
  if (!scheduleDate) {
    return false;
  }
  const dosDay = DateTime.fromISO(scheduleDate).startOf("day").toISODate();
  const today = now.startOf("day").toISODate();
  return !!dosDay && dosDay === today;
}

/** Sync check-in fields from a fresh encounter onto the procedure-room listing row. */
export function syncProcedureRoomListingCheckInFromEncounter(
  listing: ProcedureRoomListing,
  encounter: Appointment | null | undefined
): ProcedureRoomListing {
  if (!encounter) {
    return listing;
  }
  const actualCheckIn = String(
    encounter.actualCheckIn ?? encounter.encounterSchedule?.actualCheckIn ?? ""
  ).trim();
  if (actualCheckIn) {
    listing.actualCheckIn = actualCheckIn;
  }
  return listing;
}
